<template>
    <RouterLink
        class="route-button"
        :class="route.serviceType ? route.serviceType.class : ''"
        :to="{ name: routeName, params: routeParams }"
    >
        <div class="route-button-content">
            <div class="route-number-wrapper">
                <span class="no-route">
                    <span class="visuallyhidden">{{ t('route') }}&#32;</span>
                    {{ route.noParcours }}
                </span>
                <span v-if="route.accessible" class="accessible-icon">
                    <img src="@@/img/accessible.svg" width="18" height="18" />
                    <span class="visuallyhidden">{{ $t('a11y.accessible.accessible') }}</span>
                </span>
                <span v-else class="visuallyhidden">{{ $t('a11y.accessible.not_accessible') }}</span>
                <span v-if="hasNotices" class="notice-icon">
                    <img src="@@/img/alert.svg" width="14" height="14" />
                    <span class="visuallyhidden">{{ t('with_notices') }}</span>
                </span>
                <span v-if="direction" class="unidirectional">{{ direction }}</span>
            </div>
            <div class="item-wrapper" :class="{ 'departure-item': station }">
                <button v-if="ntrEnabled === false" type="button" class="btn-refresh" @click="emit('refresh')">
                    <IconRefresh width="20" height="20" />
                </button>
                <template v-else-if="station">
                    <RealTimeItem
                        v-if="station.horaires && station.horaires.length != 0 && !station.descenteSeulement"
                        :departure="station.horaires[0]"
                    ></RealTimeItem>
                    <div v-else class="no-departures">
                        <template v-if="station.arretNonDesservi">
                            {{ t('unserved_stop') }}
                        </template>
                        <template v-else-if="station.descenteSeulement">
                            {{ t('drop_off_only') }}
                        </template>
                        <template v-else>
                            {{ t('service_over') }}
                        </template>
                    </div>
                </template>
                <IconChevron class="chevron-icon" width="24" height="24" />
            </div>
        </div>
    </RouterLink>
</template>
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import { useSchedulesStore } from '@@/js/stores/schedules';
import RealTimeItem from '@/shared/RealTimeItem';

import IconChevron from '@@/icons/chevron.svg';
import IconRefresh from '@@/icons/refresh.svg';

defineEmits(['refresh']);

const props = defineProps({
    index: {
        type: Number,
        default: 1,
    },
    length: {
        type: Number,
        default: 1,
    },
    direction: {
        type: String,
        default: '',
    },
    route: {
        type: Object,
        default() {
            return {};
        },
    },
    routeName: {
        type: String,
        default: 'route-stop',
    },
    ntrEnabled: {
        type: Boolean,
        default: null,
    },
    station: {
        type: Object,
        default() {
            return null;
        },
    },
    routeParams: {
        type: Object,
        default() {
            return {};
        },
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            route: 'Parcours',
            with_notices: 'avec avis',
            unserved_stop: 'Arrêt non desservi',
            drop_off_only: 'Descente seulement',
            service_over: 'Service terminé',
        },
        en: {
            route: 'Route',
            with_notices: 'with notices',
            canceled_departure: 'Cancelled departure',
            drop_off_only: 'Drop off only',
            service_over: 'No more buses',
        },
    },
});

const { hasRouteNotices } = useSchedulesStore();
const hasNotices = computed(() => hasRouteNotices(props.route.noParcours, props.route.codeDirection));
</script>
<style lang="scss" scoped>
.route-button {
    @include rem-typo(24px);
    @include accessible-focus;
    width: 100%;
    text-align: left;
    text-decoration: none;
    color: $color-white;
}
@include colorize {
    .route-button-content {
        background-color: get-color-variation('base');
        &:hover {
            background-color: get-color-variation('hover');
        }
    }
}
.route-button-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid rgba($color-white, 0.7);
    transition: background-color $default-transition;
}
.route-number-wrapper {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
}
.no-route {
    @include rem-typo(18px);
    display: inline-block;
    margin-right: 12px;
    font-weight: 800;
    @include breakpoint(medium) {
        @include rem-typo(24px);
    }
}
.accessible-icon,
.notice-icon {
    display: flex;
    margin-right: 8px;
    flex-shrink: 0;
    img {
        @include breakpoint(medium) {
            width: 20px;
            height: 20px;
        }
    }
}
.no-departures {
    @include rem-typo(10px);
    width: 32px;
    display: flex;
    align-items: center;
    @include breakpoint(medium) {
        @include rem-typo(12px);
        width: 40px;
    }
}
.chevron-icon {
    border-radius: 50%;
    flex-shrink: 0;
    padding: 4px;
    @include breakpoint(medium) {
        padding: 8px;
        width: 30px;
        height: 30px;
    }
}
.item-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-left: 16px;
    width: 100px;
    @include breakpoint(medium) {
        width: 110px;
    }
}
.departure-item {
    border-left: 1px solid rgba($color-white, 0.4);
    flex-shrink: 0;
}
.unidirectional {
    @include rem-typo(14px);
    @include breakpoint(medium) {
        @include rem-typo(16px);
    }
}
.btn-refresh {
    @include rem-typo(14px);
    margin-left: 8px;
    padding: 4px;
    color: $color-white;
    svg {
        fill: $color-white;
    }
}
</style>
